import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './TopBar.css';

/** Dialogo de confirmación simple (sin window.confirm) */
function ConfirmDialog({ open, text, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="confirmOverlay" onClick={onCancel}>
      <div className="confirmBox" onClick={(e) => e.stopPropagation()}>
        <div style={{ marginBottom: 8 }}>{text}</div>
        <div className="confirmActions">
          <button className="btn" onClick={onCancel}>Cancelar</button>
          <button className="btn danger" onClick={onConfirm}>Aceptar</button>
        </div>
      </div>
    </div>
  );
}

export default function TopBar({
  qualityName,
  onChangeQuality,
  onAdmin,
  onOpenAdminLogin,
  onOpenShowcase,
  showQualityControls = true,
}) {
  const { user, isAdmin, loginGoogle, logout } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  const instancia = (process.env.REACT_APP_INSTANCIA || 'base').toLowerCase().trim();
  const isFabricante = instancia === 'fabricante';

  // Codigo a modificar segun la instancia
  const fabricanteLogo = '/dk.png';
  const fabricanteNombre = process.env.REACT_APP_FABRICANTE_NOMBRE || 'Dekam';

  const helpUrl =
    isFabricante
      ? 'https://www.devcode73.com.ar/ayuda-cocinaplay-fabricante'
      : 'https://www.devcode73.com.ar/ayuda-cocinaplay';

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 640) setMobileMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        setMoreOpen(false);
      }
    };
    // cierra el menú "Más" al hacer click fuera
    const onClickOutside = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, []);

  const closeMobileMenu = () => setMobileMenuOpen(false);
  const closeMore = () => setMoreOpen(false);

  const openHelp = () => {
    closeMobileMenu();
    closeMore();
    window.open(helpUrl, '_blank', 'noopener,noreferrer');
  };

  const runNewProject = () => {
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith('kitchen.')) localStorage.removeItem(k);
      }
    } catch {}
    window.location.reload();
  };

  const handleNewProject = () => {
    closeMobileMenu();
    setConfirmOpen(true);
  };

  const handleShowcase = () => {
    closeMobileMenu();
    onOpenShowcase?.();
  };

  const handleChangeQuality = () => {
    closeMobileMenu();
    onChangeQuality?.();
  };

  const handleAdminLogin = () => {
    closeMobileMenu();
    closeMore();
    onOpenAdminLogin?.();
  };

  const handleAdmin = () => {
    closeMobileMenu();
    onAdmin?.();
  };

  const handleLogin = () => {
    closeMobileMenu();
    loginGoogle?.();
  };

  const handleLogout = () => {
    closeMobileMenu();
    logout?.();
  };

  return (
    <>
      <header className={`topbar ${mobileMenuOpen ? 'is-mobile-open' : ''}`}>
        {/* Marca */}
        <div className="topbar__brand">
          <div className="topbar__brandMain">
            <img
              src="/CocinaPlayLogo2.png"
              className="topbar__brandImg"
              alt="Logo Cocina Play"
            />
            <span className="topbar__betaTag">Beta</span>
          </div>

          {isFabricante && fabricanteLogo && (
            <div className="topbar__brandPartner">
              <span className="topbar__brandDivider" aria-hidden="true" />
              <img
                src={fabricanteLogo}
                className="topbar__partnerImg"
                alt={fabricanteNombre}
                title={fabricanteNombre}
              />
            </div>
          )}
        </div>

        {/* Toggle mobile */}
        <button
          type="button"
          className={`topbar__mobileToggle ${mobileMenuOpen ? 'is-open' : ''}`}
          onClick={() => setMobileMenuOpen((v) => !v)}
          aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={mobileMenuOpen}
        >
          <span />
          <span />
          <span />
        </button>

        {/* Desktop - barra de herramientas */}
        <div className="topbar__tools">
          {/* Grupo: proyecto / diseño */}
          <div className="topbar__group">
            <button
              className="btn outline"
              onClick={handleNewProject}
              title="Borra el proyecto y reinicia"
            >
              Nuevo proyecto
            </button>

            {showQualityControls && (
              <>
                <div className="topbar__qualityBadge" title="Calidad seleccionada">
                  <span className="dot" />
                  <span className="label">
                    Calidad: <strong>{qualityName || '—'}</strong>
                  </span>
                </div>

                <button className="btn ghost" onClick={handleChangeQuality}>
                  Cambiar calidad
                </button>
              </>
            )}

            <button
              className="btn primary"
              onClick={handleShowcase}
              title="Ver ejemplos de diseños y renders"
              aria-label="Ver ejemplos y renders"
            >
              Inspírate ✨
            </button>
          </div>

          <span className="topbar__divider" aria-hidden="true" />

          {/* Grupo: cuenta / secundarios */}
          <div className="topbar__group">
            <div className="topbar__more" ref={moreRef}>
              <button
                type="button"
                className="btn outline"
                onClick={() => setMoreOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={moreOpen}
                title="Más opciones"
              >
                Más ▾
              </button>

              {moreOpen && (
                <div className="topbar__moreMenu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className="topbar__moreItem"
                    onClick={openHelp}
                    title="Abrir centro de ayuda"
                  >
                    Ayuda
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="topbar__moreItem"
                    onClick={handleAdminLogin}
                    title="Ingresar como administrador"
                  >
                    Acceso admin
                  </button>
                </div>
              )}
            </div>

            {user && isAdmin && (
              <button className="btn primary" onClick={handleAdmin}>
                Configuración
              </button>
            )}

            {!user ? (
              <button className="btn primary" onClick={handleLogin}>
                Iniciar sesión
              </button>
            ) : (
              <div className="topbar__user">
                <img
                  className="topbar__avatar"
                  src={
                    user.photoURL ||
                    'https://ui-avatars.com/api/?background=ddd&name=' +
                      encodeURIComponent(user.displayName || 'U')
                  }
                  alt={user.displayName || user.email || 'Usuario'}
                />
                <button className="btn danger outline" onClick={handleLogout}>
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Desktop tagline */}
        <div className="topbar__tagline">
          Diseñá fácil y jugando tu cocina modular en 2D. Renderizá con IA probando distintos acabados.
        </div>

        {/* Mobile panel */}
        <div className="topbar__mobilePanel">
          <div className="topbar__mobileTagline">
            Diseñá fácil y jugando tu cocina modular en 2D. Renderizá con IA probando distintos acabados.
          </div>

          <div className="topbar__mobileSection">
            <button
              className="btn outline"
              onClick={handleNewProject}
              title="Borra el proyecto y reinicia"
            >
              Nuevo proyecto
            </button>

            <button
              className="btn primary"
              onClick={handleShowcase}
              title="Ver ejemplos de diseños y renders"
              aria-label="Ver ejemplos y renders"
            >
              Inspírate ✨
            </button>

            {showQualityControls && (
              <>
                <div className="topbar__qualityBadge topbar__qualityBadge--mobile" title="Calidad seleccionada">
                  <span className="dot" />
                  <span className="label">
                    Calidad: <strong>{qualityName || '—'}</strong>
                  </span>
                </div>

                <button className="btn ghost" onClick={handleChangeQuality}>
                  Cambiar calidad
                </button>
              </>
            )}

            <button
              className="btn outline"
              onClick={openHelp}
              title="Abrir centro de ayuda"
              aria-label="Abrir ayuda"
            >
              Ayuda
            </button>

            <button
              className="btn outline"
              onClick={handleAdminLogin}
              title="Ingresar como administrador"
            >
              Acceso admin
            </button>

            {user && isAdmin && (
              <button className="btn primary" onClick={handleAdmin}>
                Configuración
              </button>
            )}

            {!user ? (
              <button className="btn primary" onClick={handleLogin}>
                Iniciar sesión
              </button>
            ) : (
              <div className="topbar__mobileUser">
                <img
                  className="topbar__avatar"
                  src={
                    user.photoURL ||
                    'https://ui-avatars.com/api/?background=ddd&name=' +
                      encodeURIComponent(user.displayName || 'U')
                  }
                  alt={user.displayName || user.email || 'Usuario'}
                />
                <button className="btn danger outline" onClick={handleLogout}>
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <ConfirmDialog
        open={confirmOpen}
        text="Esto borrará tu proyecto guardado en este navegador y reiniciará la app. ¿Continuar?"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={runNewProject}
      />
    </>
  );
}